import { gql } from "lib/apolloClient";

export const basicUserDetailsFragment = gql`
  fragment BasicUserDetailsFragment on Staff {
    id
    firstName
    lastName
    middleName
    prefix
    suffix
    pronouns
    preferredName
    profileImage
  }
`;

export const globalCategoryFragment = gql`
  fragment GlobalCategoryFragment on GlobalCategory {
    id
    name
    insights {
      categoryValueCount
    }
    createdBy {
      ...BasicUserDetailsFragment
    }
    updatedBy {
      ...BasicUserDetailsFragment
    }
  }
  ${basicUserDetailsFragment}
`;

export const gradingConfigurationFragment = gql`
  fragment GradingConfigurationFragment on GradingConfiguration {
    id
    type
    settings {
      id
      uid
      groupType
      value
    }
  }
`;

export const gradingCategorySetsFragmentWithGradeScaleValues = gql`
  fragment GradingCategorySetsFragmentWithGradeScaleValues on GradingConfiguration {
    id
    type
    gradingCategorySets {
      id
      gradingCategoryValues {
        id
        globalCategory {
          id
          name
        }
        gradeScale {
          id
          displayName
          templateName
          gradeScaleEntityDetails(filters: $gradeScaleEntityDetailsFilters) {
            id
            gradeScaleValues {
              id
              abbreviation
              label
              displaySequence
              gradeScaleValueScores {
                id
              }
            }
          }
        }
        valueDetails {
          id
          weight
          subjectLevel {
            id
            label
            abbreviation
          }
        }
      }
      academicTerm {
        id
        label
      }
    }
  }
`;

export const gradingCategorySetsFragment = gql`
  fragment GradingCategorySetsFragment on GradingConfiguration {
    id
    type
    gradingCategorySets {
      id
      gradingCategoryValues {
        id
        globalCategory {
          id
          name
        }
        gradeScale {
          id
          displayName
          templateName
          gradeScaleEntityDetails {
            id
            isDefault
          }
        }
        valueDetails {
          id
          weight
          subjectLevel {
            id
            label
            abbreviation
          }
        }
      }
      academicTerm {
        id
        label
      }
    }
  }
`;

export const gradeScaleFragment = gql`
  fragment GradeScaleFragment on GradeScale {
    id
    displayName
    templateName
    type
    dependentGradeScales {
      id
      type
    }
    gradeScaleEntityDetails(filters: $gradeScaleEntityDetailsFilters) {
      id
      entityDetail {
        learningCourseId
        subjectId
        gradeId
        academicTermId
      }
      academicTerm {
        id
        label
      }
      gradeScaleValues {
        id
        abbreviation
        label
        displaySequence
        gradeScaleValueScores {
          id
        }
      }
      gradingConfiguration {
        settings {
          id
          uid
          groupType
          value
        }
      }
    }
  }
`;

export const cumulativeTermWeightsFragment = gql`
  fragment CumulativeTermWeightsFragment on GradingConfiguration {
    id
    cumulativeTermWeights {
      cumulativeTermId
      associatedTermWeights {
        termId
        weight
      }
    }
  }
`;

export const gradeScaleValueScoresFragment = gql`
  fragment GradeScaleValueScoresFragment on GradeScaleValueScore {
    id
    subjectLevel {
      id
      label
      abbreviation
    }
    minValue
    maxValue
    gradeValue
    isMasteryLevel
  }
`;

export const gradeScaleValuesFragment = gql`
  fragment GradeScaleValuesFragment on GradeScaleValue {
    id
    abbreviation
    label
    description
    displaySequence
    color
    type
    gradeScaleValueScores {
      ...GradeScaleValueScoresFragment
    }
    gradeScaleValueGradePoints {
      id
      gradePoint
      isAddedValueApplicable
    }
    dependentGradeScaleValues {
      id
      label
      abbreviation
      gradeScale {
        id
        displayName
        templateName
      }
    }
  }
  ${gradeScaleValueScoresFragment}
`;

export const gradeScaleEntityDetailsFragment = gql`
  fragment GradeScaleEntityDetailFragment on GradeScaleEntityDetail {
    id
    isPrimary
    gradeScaleValues {
      ...GradeScaleValuesFragment
    }
    isDefault
    academicTerm {
      id
      label
    }
    entityDetail {
      subjectId
      gradeId
      academicTermId
      learningCourseId
    }
    gradeValueCalculateTypeDetail {
      id
      gradeValueCalculateType
      subjectLevel {
        id
      }
    }
    gradingConfiguration {
      settings {
        id
        uid
        groupType
        value
      }
    }
    insights {
      gradeScaleToolCount
    }
  }
  ${gradeScaleValuesFragment}
`;

export const gradeScaleDetailFragment = gql`
  fragment GradeScaleFragment on GradeScale {
    id
    templateUid
    templateName
    displayName
    description
    type
    gradingType
    minValue
    maxValue
    calculateStandardGradesUsing
    isUseForMastery
    isUseForCreditGPACalculation
    metadata
    specialGradeTemplate {
      id
      displayName
      templateName
    }
    dependentGradeScales {
      id
      displayName
      templateName
    }
    insights {
      learningCourseCount
      standardSetCount
      subjectCount
      subjectGradeMapCount
      usedAsSpecialGradeScaleCount
    }
    createdBy {
      ...BasicUserDetailsFragment
    }
    updatedBy {
      ...BasicUserDetailsFragment
    }
    updatedAt
    isArchived
    gradeScaleEntityDetails(filters: $gradeScaleEntityDetailsFilters) {
      ...GradeScaleEntityDetailFragment
    }
  }
  ${basicUserDetailsFragment}
  ${gradeScaleEntityDetailsFragment}
`;

export const compactGradeScaleDetailFragment = gql`
  fragment GradeScaleFragment on GradeScale {
    id
    type
    templateName
    displayName
    updatedBy {
      ...BasicUserDetailsFragment
    }
    updatedAt
    gradeScaleEntityDetails(filters: $gradeScaleEntityDetailsFilters) {
      id
      isPrimary
      academicTerm {
        id
        label
      }
      entityDetail {
        subjectId
        gradeId
        academicTermId
        learningCourseId
      }
      gradingConfiguration {
        settings {
          id
          uid
          groupType
          value
        }
      }
    }
  }
  ${basicUserDetailsFragment}
`;

export const gradingConfigurationWrapperFragment = gql`
  fragment GradingConfigurationWrapperFragment on GradingConfigurationWrapper {
    gradeScale {
      id
    }
    gradingConfiguration {
      id
      settings {
        id
        uid
        groupType
        value
      }
      createdBy {
        ...BasicUserDetailsFragment
      }
      updatedBy {
        ...BasicUserDetailsFragment
      }
      updatedAt
    }
  }
  ${basicUserDetailsFragment}
`;

export const standardSetWrapperFragment = gql`
  fragment StandardSetWrapperFragment on StandardSetWrapper {
    gradeScales {
      ...GradeScaleFragment
    }
  }
  ${gradeScaleDetailFragment}
`;

export const plannerElementNodeSetFragment = gql`
  fragment PlannerElementNodeSetFragment on PlannerElementNodeSet {
    id
    title
    standardSetWrapper {
      ...StandardSetWrapperFragment
    }
    gradingConfigurationWrapper {
      ...GradingConfigurationWrapperFragment
    }
  }
  ${standardSetWrapperFragment}
  ${gradingConfigurationWrapperFragment}
`;

export const levelsDetailFragment = gql`
  fragment LevelsDetailFragment on GenericTag {
    id
    label
    abbreviation
    reportDisplayName
    type
    isArchived
  }
`;

export const gradeScaleDetailFragmentWithPresetScoreValues = gql`
  fragment GradeScaleFragment on GradeScale {
    id
    templateUid
    templateName
    displayName
    description
    type
    gradingType
    minValue
    maxValue
    calculateStandardGradesUsing
    isUseForMastery
    isUseForCreditGPACalculation
    metadata
    specialGradeTemplate {
      id
      displayName
      templateName
    }
    dependentGradeScales {
      id
      displayName
      templateName
    }
    isArchived
    gradeScaleEntityDetails(filters: $gradeScaleEntityDetailsFilters) {
      id
      isPrimary
      gradeScaleValues {
        id
        abbreviation
        label
        description
        displaySequence
        color
        type
        gradeScaleValueScores {
          id
          subjectLevel {
            id
            label
            abbreviation
          }
          minValue
          maxValue
          gradeValue
          isMasteryLevel
          presetGradeScaleValueScore {
            id
            subjectLevel {
              id
            }
            minValue
            maxValue
            gradeValue
          }
        }
        dependentGradeScaleValues {
          id
          label
          abbreviation
          gradeScale {
            id
            displayName
            templateName
          }
        }
      }
      isDefault
      academicTerm {
        id
        label
      }
      entityDetail {
        subjectId
        gradeId
        academicTermId
        learningCourseId
      }
      gradeValueCalculateTypeDetail {
        id
        gradeValueCalculateType
        subjectLevel {
          id
        }
      }
    }
  }
`;

export const ibGradeScaleCategoryFragment = gql`
  fragment IBGradeScaleCategoryFragment on GradingConfiguration {
    id
    settings {
      id
      uid
      groupType
      value
    }
    gradingCategorySets {
      id
      academicTerm {
        id
      }
      gradingCategoryValues {
        id
        globalCategory {
          id
        }
        gradeScale {
          id
          type
          gradeScaleEntityDetails {
            id
            isDefault
            gradeScaleValues {
              id
              label
              abbreviation
              displaySequence
              gradeScaleValueScores {
                id
                subjectLevel {
                  id
                  label
                  abbreviation
                }
                minValue
                maxValue
                gradeValue
              }
            }
          }
        }
      }
    }
  }
`;

export const ibGradeScaleTermFragment = gql`
  fragment IBGradeScaleTermFragment on GradeScale {
    id
    type
    gradeScaleEntityDetails {
      id
      academicTerm {
        id
      }
      gradeScaleValues {
        id
        label
        displaySequence
        abbreviation
        gradeScaleValueScores {
          id
          subjectLevel {
            id
            label
            abbreviation
          }
          minValue
          maxValue
          gradeValue
        }
      }
    }
  }
`;
